// Imports
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AnimateHeight from 'react-animate-height';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { GeneratedThemeProps } from '../../../templates/template-style';
import { Link, useLocation } from 'react-router-dom';
import PagesMenu from '../../content/pages-menu';
import { useAppDispatch, useAppSelector } from '../../../../shareables/foundation/front-end/redux/hooks';
import notificationBoxItems from '../notification-box-items';
import SignIn from './sign-in';
import UserMenu from './user-menu';
import selfActions from '../../../../redux/self/actions';
import helperFunctions from '../../../../shareables/foundation/front-end/utils/helper-functions';
import ScreenSharingIndicator from '../screen-sharing-indicator';
import keyValueStorage from '../../../../shareables/foundation/front-end/utils/key-value-storage';
import useMemberCheck from '../../../../utils/use-member-check';


// Styled components
const Container = styled.div`
	padding-bottom: 0.375rem;
	
	@media only screen and (min-width: 48rem) {
		display: none;
	}
`;

const TopRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding-left: 0.9375rem;
`;

const MenuIcon = styled.span`
	font-size: 1.875rem;
	cursor: pointer;
	margin-left: auto;
	
	display: inline-block;
	text-align: right;
	padding-right: 0.9375rem;
	line-height: 3.125rem;
	
	svg {
		transform: translateY(0.1875rem);
	}
`;

const boxStyle = ({ theme }: { theme: GeneratedThemeProps }) => `
	background: ${theme.isPaper ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.2)'};
	max-width: 48rem;
	margin: 0.375rem 0.9375rem 1.25rem;
	padding: 3.5%;
	
	a:hover, a:focus{
		text-decoration: none;
	}
	
	&& a {
		color: ${theme.isPaper ? '#000' : '#fff'};
	}
`;

const UserBox = styled.div<{ theme: GeneratedThemeProps }>`
	${boxStyle}
	
	text-align: left;
	display: grid;
	grid-template-columns: 1fr 3fr;
`;

const NotificationBox = styled.div<{ theme: GeneratedThemeProps }>`
	${boxStyle}
	text-align: center;
	
	ul {
		list-style-type: none;
		font-size: 1rem;
		line-height: 1.5rem;
	}
`;

const AdminModeBox = styled.div`
	${boxStyle}
	
	text-align: center;
	font-size: 1rem;
	line-height: 1.5rem;
	text-decoration: underline;
	cursor: pointer;
`;

const PagesWrapper = styled.div<{ theme: GeneratedThemeProps }>`
	text-align: left;
	margin: 0.9063rem 0.9375rem 0.25rem;
	
	h3,
	li,
	a {
		color: ${({ theme }) => (theme.isPaper ? '#000' : '#fff')};
	}
	
	.active {
		background: ${({ theme }) => (theme.isPaper ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)')};
	}
	
	h3 {
		font-size: 1.75rem;
		margin-bottom: 0.375rem;
	}
	
	li:first-child {
		margin-top: -0.375rem;
	}
	
	li {
		border-bottom: none;
		font-size: 1.125rem;
		line-height: 1.125rem;
		
		&:hover {
			background: ${({ theme }) => (theme.isPaper ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)')};
			
			&:has(a.active) {
				background: transparent;
			}
		}
	}
	
	.svg-inline--fa:not(.fa-chevron-right) {
		color: rgba(255, 255, 255, 0.4);
	}
`;


// Define the accepted props
interface Props {
	storybookIsOpen?: true;
	storybookSharingActive?: true;
}


// Function component
const MobileNavigation: React.FC<Props> = ({ storybookIsOpen, storybookSharingActive }) => {
	// Use state
	const [isOpen, setIsOpen] = useState(storybookIsOpen ?? false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const user = useAppSelector((state) => state.common.user);
	const member = useAppSelector((state) => state.self.member);
	const adminView = useAppSelector((state) => state.self.adminView);
	const isAdmin = useMemberCheck({ type: 'ANY_PRIVILEGE' });
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Close menu on navigation change
	useEffect(() => {
		if (!(window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			setIsOpen(false);
		}
	}, [location]);
	
	
	// Generate notification box items JSX
	const notificationBoxItemsJSX: JSX.Element[] = [];
	
	if (member) {
		notificationBoxItems(member).map((item) => {
			notificationBoxItemsJSX.push(
				<li key={item.uniqueKey}>
					<Link to={item.to}>{item.content}</Link>
				</li>
			);
		});
	}
	
	
	// Return JSX
	return (
		<Container>
			<TopRow>
				<ScreenSharingIndicator storybookSharingActive={storybookSharingActive} />
				
				<MenuIcon onClick={() => setIsOpen((prevState) => !prevState)}>
					{isOpen ? <FontAwesomeIcon icon={faChevronUp} /> : `☰`}
				</MenuIcon>
			</TopRow>
			
			<AnimateHeight duration={300} height={isOpen ? 'auto' : 0}>
				<UserBox>{user.signedIn ? <UserMenu /> : <SignIn />}</UserBox>
				
				{user.signedIn && notificationBoxItemsJSX.length > 0 && (
					<NotificationBox>
						<ul>{notificationBoxItemsJSX}</ul>
					</NotificationBox>
				)}
				
				{isAdmin && !adminView && (
					<AdminModeBox
						{...helperFunctions.buttonize(() => {
							keyValueStorage.removeItem('viewing_as_non_admin');
							dispatch(selfActions.setAdminView(true));
							setIsOpen(false);
						})}
					>
						Re-enter admin mode
					</AdminModeBox>
				)}
				
				<PagesWrapper>
					<PagesMenu />
				</PagesWrapper>
			</AnimateHeight>
		</Container>
	);
};

export default MobileNavigation;
